import React from 'react';
import {useParams} from 'react-router';
import {useMutation, useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import ReactDocumentTitle from '../../../components/ReactDocumentTitle';
import AppLayout from '../../../layouts/AppLayout';
import BackLink from '../../../ui/BackLink';
import {handleError, handleSuccess} from '../../../utils/Apollo';
import FormTitle from '../../../ui/FormTitle';
import AgreementForm from '../../../components/licenseType/AgreementForm';
import {LicenseAgreement} from '../../../models/brand/LicenseAgreement';
import Preloader from '../../../ui/Preloader';
import {LicenseType} from '../../../models/brand/LicenseType';
import {reverse} from '../../../urls';
import Menu from '../../../components/licenseType/Menu';
import FormWrapper from '../../../components/licenseType/FormWrapper';
import BackForward from '../../../components/licenseType/BackForward';

interface PathParams {
  id: string
  licenseTypeId: string
}

interface LicenseAgreementQuery {
  agreement: LicenseAgreement
  licenseType: LicenseType
}

export default function EditAgreementScene() {
  const {id, licenseTypeId} = useParams<PathParams>();
  const {loading, data, error} = useQuery<LicenseAgreementQuery>(query, {
    variables: {id, licenseTypeId},
    errorPolicy: 'all'
  });
  const [submit] = useMutation(mutation);
  const [deleteMe] = useMutation(deleteMutation);
  const backLink = reverse('brand_settings_license_types');

  const onFormSubmit = React.useCallback((formData) => {
    submit({
      variables: {data: formData, id}
    }).then(
      () => handleSuccess('License agreement updated', reverse('license_renewal_period_edit', {id: licenseTypeId})),
      (apolloError) => handleError(apolloError)
    );
  }, [submit, licenseTypeId, id]);

  const onDelete = React.useCallback(() => {
    deleteMe({
      variables: {id}
    }).then(
      () => handleSuccess('License agreement deleted', reverse('license_agreement_add', {licenseTypeId})),
      (apolloError) => handleError(apolloError)
    );
  }, [deleteMe, licenseTypeId, id]);

  return (
    <>
      <ReactDocumentTitle title="Edit license agreement"/>
      <AppLayout className="bg-gray-light">
        <BackLink backLink={reverse('brand_settings_license_types')} title="Back to settings"/>
        {error && <Preloader/>}
        {loading && <Preloader/>}
        {!loading && !error &&
          <>
            <Menu activeIndex={3}/>
            <FormWrapper>
              <FormTitle>Edit license agreement</FormTitle>
              <AgreementForm onSubmit={onFormSubmit} backLink={backLink} agreement={data?.agreement}
                             onDelete={onDelete}/>
              <BackForward back={
                {url: reverse('license_renewal_templates_edit', {licenseTypeId}), label: 'Edit Notifications'}}
                           forward={
                             {
                               url: reverse('license_renewal_period_edit', {id: licenseTypeId}),
                               label: 'Edit Renewal Period'
                             }}
              />
            </FormWrapper>
          </>
        }
      </AppLayout>
    </>
  );
}

const query = gql`
  query EditLicenseAgreementQuery($id: ID!, $licenseTypeId: ID!) {
    licenseType(id: $licenseTypeId) {
      name
    },
    agreement: licenseAgreement(id: $id) {
      id
      version
      content
      consent
    }
  }
`;

const mutation = gql`
  mutation UpdateFormMutation($id: ID!, $data: LicenseAgreementInput!) {
    updateLicenseAgreement(id: $id, data: $data) {
      id
      version
      content
      consent
    }
  }
`;


const deleteMutation = gql`
  mutation DeleteAgreementMutation($id: ID!) {
    deleteLicenseAgreement(id: $id) {
      id
    }
  }
`;
